import { IconButton, IconType } from '@assembly-js/design-system';
import { Editor } from '@tiptap/core';
import clsx from 'clsx';
import { ComponentProps, useMemo, useState } from 'react';

import { LinkButton, LinkPopover } from './LinkPopover';
import { TextMenu } from './TextMenu';
import { ActionHandlers, createActionHandlers } from './utils';

const ACTION_KEYS = [
  'bold',
  'italic',
  'underline',
  'strike',
  'bulletList',
  'orderedList',
] as const;

type TextMarkActionKey = (typeof ACTION_KEYS)[number];

interface TextMarkAction extends ActionHandlers {
  icon: IconType;
  isActive: () => boolean;
}

interface EditorMenuProps {
  editor: Editor;
  textMenuActions?: ComponentProps<typeof TextMenu>['actions'];
}

export const EditorMenu = ({ editor, textMenuActions }: EditorMenuProps) => {
  const installedExtensions = useMemo(
    () =>
      new Set(
        editor.extensionManager.extensions.map((extension) => extension.name),
      ),
    [editor],
  );

  const textMarkActions = useMemo(
    (): Record<TextMarkActionKey, TextMarkAction> => ({
      bold: {
        icon: 'Bold',
        isActive: () => editor.isActive('bold'),
        ...createActionHandlers(editor, (chain) =>
          chain.focus().toggleBold().run(),
        ),
      },
      italic: {
        icon: 'Italicize',
        isActive: () => editor.isActive('italic'),
        ...createActionHandlers(editor, (chain) =>
          chain.focus().toggleItalic().run(),
        ),
      },
      underline: {
        icon: 'Underline',
        isActive: () => editor.isActive('underline'),
        ...createActionHandlers(editor, (chain) =>
          chain.focus().toggleUnderline().run(),
        ),
      },
      strike: {
        icon: 'Strikethrough',
        isActive: () => editor.isActive('strike'),
        ...createActionHandlers(editor, (chain) =>
          chain.focus().toggleStrike().run(),
        ),
      },
      bulletList: {
        icon: 'UnorderedList',
        isActive: () => editor.isActive('bulletList'),
        ...createActionHandlers(editor, (chain) =>
          chain.focus().toggleBulletList().run(),
        ),
      },
      orderedList: {
        icon: 'NumberedList',
        isActive: () => editor.isActive('orderedList'),
        ...createActionHandlers(editor, (chain) =>
          chain.focus().toggleOrderedList().run(),
        ),
      },
    }),
    [],
  );

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  return (
    <div
      className={clsx(
        'h-10 bg-white rounded-md border border-solid border-secondary-hover shadow-dropdown-menu flex items-center overflow-hidden',
        // set border styling for children
        '[&>*]:border-0 [&>*:not(:last-child)]:border-r [&>*:not(:last-child)]:border-secondary-hover',
      )}
    >
      {installedExtensions.has('heading') &&
        installedExtensions.has('paragraph') && (
          <TextMenu editor={editor} actions={textMenuActions} />
        )}
      {ACTION_KEYS.map((key) => {
        const { icon, isActive, action, canRunAction } = textMarkActions[key];
        return (
          <IconButton
            key={key}
            label={key}
            icon={icon}
            onClick={action}
            className="inline-flex items-center justify-center rounded-none w-10 h-10"
            variant={isActive() ? 'primary' : 'secondary'}
            disabled={!canRunAction()}
          />
        );
      })}
      {installedExtensions.has('link') && (
        <>
          <LinkButton onClick={(e) => setAnchorEl(e.currentTarget)} />
          <LinkPopover
            editor={editor}
            onClose={() => setAnchorEl(null)}
            anchorEl={anchorEl}
          />
        </>
      )}
    </div>
  );
};
